import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { base_api_url } from "../../../environment.jsx";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Image, X } from "lucide-react";
import "./AddIngredient.css";

const AddIngredient = ({ show, onClose, onSuccess, editMode = false, ingredientData = null }) => {
    const MySwal = withReactContent(Swal);
    const [loading, setLoading] = useState(false);
    const imageRef = useRef();
    const [logo, setLogo] = useState(null);

    // Debug props
    useEffect(() => {
        console.log("AddIngredient props:", { show, editMode, ingredientData });
    }, [show, editMode, ingredientData]);

    const ideresto = JSON.parse(localStorage.getItem("user"))?.restoId;
    const token = JSON.parse(localStorage.getItem("user"))?.access_token;
    const id_utilisateur = JSON.parse(localStorage.getItem("user"))?.id_utilisateur;

    // Listes des catégories et unités
    const categories = [
        "Viandes",
        "Poissons",
        "Légumes",
        "Fruits",
        "Epices",
        "Féculents",
        "Produits laitiers",
        "Boissons",
        "Packaging",
        "Autres"
    ];

    const unites = [
        { label: "Grammes (g)", value: "g" },
        { label: "Kilogrammes (kg)", value: "kg" },
        { label: "Millilitres (mL)", value: "mL" },
        { label: "Litres (L)", value: "L" },
        { label: "Pièce", value: "pièce" }
    ];

    const [formData, setFormData] = useState({
        nom: "",
        code: "",
        categorie: "",
        unite: "",
        prix_unitaire: "",
        quantite_initiale: "",
        seuil_alerte: "10",
        image: "",
        id_restaurant: ideresto
    });

    const resetForm = () => {
        // Libérer la mémoire blob si elle existe
        if (logo && logo.startsWith('blob:')) {
            URL.revokeObjectURL(logo);
        }
        
        setFormData({
            nom: "",
            code: "",
            categorie: "",
            unite: "",
            prix_unitaire: "",
            quantite_initiale: "",
            seuil_alerte: "10",
            image: "",
            id_restaurant: ideresto
        });
        setLogo(null);
        if (imageRef.current) {
            imageRef.current.value = "";
        }
    };

    // Populate form in edit mode
    useEffect(() => {
        if (editMode && ingredientData && show) {
            console.log("Populating form with:", ingredientData);
            setFormData({
                nom: ingredientData.nom || "",
                code: ingredientData.code || "",
                categorie: ingredientData.categorie || "",
                unite: ingredientData.unite || "",
                prix_unitaire: ingredientData.prix_unitaire || "",
                quantite_initiale: ingredientData.stock?.quantite || "",
                seuil_alerte: ingredientData.seuil_alerte || "10",
                image: ingredientData.image || "",
                id_restaurant: ideresto
            });
            if (ingredientData.image) {
                setLogo(`${base_api_url}api/recuperer/${ingredientData.image}`);
            }
        }
    }, [editMode, ingredientData, ideresto, show]);

    // Reset form when modal closes (only in create mode)
    useEffect(() => {
        if (!show && !editMode) {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, editMode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const blobUrl = URL.createObjectURL(file);
            setLogo(blobUrl);
            setFormData((prev) => ({
                ...prev,
                image: blobUrl
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Upload image to S3 first if a new image was selected
            let imageFileName = formData.image;
            if (logo && logo.startsWith('blob:')) {
                const imageFile = imageRef.current.files[0];
                if (imageFile) {
                    console.log("Uploading image file:", imageFile.name);
                    const imageFormData = new FormData();
                    imageFormData.append('file', imageFile);
                    imageFormData.append('nomtableconcerner', 'ingredient');
                    imageFormData.append('iduser', id_utilisateur);

                    try {
                        const imageResponse = await axios.post(
                            `${base_api_url}api/envoyer_image`,
                            imageFormData,
                            {
                                headers: {
                                    'Content-Type': 'multipart/form-data',
                                    Authorization: `Bearer ${token}`,
                                },
                            }
                        );

                        if (imageResponse.data?.nom_image) {
                            imageFileName = imageResponse.data.nom_image;
                            console.log("Image uploaded successfully:", imageFileName);
                        }
                    } catch (imageError) {
                        console.error("Error uploading image:", imageError);
                        throw new Error("Échec du téléchargement de l'image. Veuillez réessayer.");
                    }
                }
            }

            // Prepare submission data
            const submissionData = {
                nom: formData.nom,
                code: formData.code,
                categorie: formData.categorie,
                unite: formData.unite,
                prix_unitaire: parseFloat(formData.prix_unitaire),
                seuil_alerte: parseInt(formData.seuil_alerte),
                id_restaurant: ideresto
            };

            // Add quantite_initiale only for creation
            if (!editMode) {
                submissionData.quantite_initiale = parseFloat(formData.quantite_initiale);
            }

            // Add image if uploaded or exists
            if (imageFileName) {
                submissionData.image = imageFileName.replace(`${base_api_url}api/recuperer/`, '');
            }

            console.log("Submitting ingredient data:", submissionData);

            let response;
            if (editMode && ingredientData?.id_ingredient) {
                // Update existing ingredient
                response = await axios.put(
                    `${base_api_url}api/ingredient/${ingredientData.id_ingredient}`,
                    submissionData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                console.log("Ingredient updated:", response.data);
            } else {
                // Create new ingredient
                response = await axios.post(
                    `${base_api_url}api/ingredient`,
                    submissionData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                console.log("Ingredient created:", response.data);
            }

            // Show success toast
            MySwal.fire({
                icon: "success",
                text: editMode 
                    ? "L'ingrédient a été modifié avec succès." 
                    : "L'ingrédient a été créé avec succès.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });

            // Reset form and close modal
            resetForm();
            onSuccess(); // Refresh the ingredient list
            onClose();

        } catch (error) {
            console.error("Error saving ingredient:", error);

            let errorMessage = editMode
                ? "Une erreur s'est produite lors de la modification de l'ingrédient."
                : "Une erreur s'est produite lors de la création de l'ingrédient.";

            if (error.response) {
                const status = error.response.status;
                const errorData = error.response.data;

                switch (status) {
                    case 400:
                        errorMessage = errorData?.message || "Données invalides. Vérifiez les champs.";
                        break;
                    case 401:
                        errorMessage = "Session expirée. Veuillez vous reconnecter.";
                        break;
                    case 404:
                        errorMessage = editMode
                            ? "L'ingrédient que vous tentez de modifier n'existe plus."
                            : "Ressource introuvable.";
                        break;
                    case 409:
                        errorMessage = "Un ingrédient avec ce code existe déjà.";
                        break;
                    case 500:
                        errorMessage = "Erreur serveur. Veuillez réessayer.";
                        break;
                    default:
                        errorMessage = errorData?.message || `Erreur ${status} lors de l'opération.`;
                }
            } else if (error.request) {
                errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
            }

            MySwal.fire({
                icon: "error",
                text: errorMessage,
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className={`ingredient-drawer-backdrop ${show ? 'show' : ''}`}
                onClick={onClose}
            ></div>

            {/* Drawer */}
            <div className={`ingredient-drawer ${show ? 'show' : ''}`}>
                <form onSubmit={handleSubmit}>
                    {/* Header */}
                    <div className="ingredient-drawer-header">
                        <h4>{editMode ? "Modifier l'ingrédient" : "Ajouter un ingrédient"}</h4>
                        <button
                            type="button"
                            className="ingredient-drawer-close-btn"
                            onClick={onClose}
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="ingredient-drawer-body">
                        {/* Section: Informations générales */}
                        <div className="ingredient-drawer-section-header">
                            Informations générales
                        </div>

                        {/* Nom de l'ingrédient */}
                        <div className="ingredient-drawer-form-group">
                            <label className="ingredient-drawer-label ingredient-drawer-label-required">
                                Nom de l&apos;ingrédient
                            </label>
                            <input
                                type="text"
                                name="nom"
                                value={formData.nom}
                                onChange={handleChange}
                                required
                                className="ingredient-drawer-input"
                                placeholder="Ex: Tomate"
                            />
                        </div>

                        {/* Code */}
                        <div className="ingredient-drawer-form-group">
                            <label className="ingredient-drawer-label ingredient-drawer-label-required">
                                Code
                            </label>
                            <input
                                type="text"
                                name="code"
                                value={formData.code}
                                onChange={handleChange}
                                required
                                className="ingredient-drawer-input"
                                placeholder="Ex: TOM001"
                            />
                            <span className="ingredient-drawer-hint">
                                Code unique pour identifier l&apos;ingrédient
                            </span>
                        </div>

                        {/* Row: Catégorie & Unité */}
                        <div className="ingredient-drawer-row">
                            <div className="ingredient-drawer-col">
                                <label className="ingredient-drawer-label ingredient-drawer-label-required">
                                    Catégorie
                                </label>
                                <select
                                    name="categorie"
                                    className="ingredient-drawer-select"
                                    value={formData.categorie}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Sélectionner</option>
                                    {categories.map((cat) => (
                                        <option key={cat} value={cat}>
                                            {cat}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="ingredient-drawer-col">
                                <label className="ingredient-drawer-label ingredient-drawer-label-required">
                                    Unité
                                </label>
                                <select
                                    name="unite"
                                    className="ingredient-drawer-select"
                                    value={formData.unite}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Sélectionner</option>
                                    {unites.map((unit) => (
                                        <option key={unit.value} value={unit.value}>
                                            {unit.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Image de l'ingrédient */}
                        <div className="ingredient-drawer-form-group">
                            <label className="ingredient-drawer-label">Image de l&apos;ingrédient</label>

                            <div
                                className="ingredient-drawer-upload-area"
                                onClick={() => imageRef.current?.click()}
                            >
                                <div className="ingredient-drawer-upload-icon">
                                    <Image size={32} />
                                </div>
                                <div className="ingredient-drawer-upload-text">
                                    Cliquez pour ajouter une image
                                </div>
                                <div className="ingredient-drawer-upload-hint">
                                    PNG, JPG jusqu&apos;à 1 Mo
                                </div>
                            </div>

                            <input
                                type="file"
                                accept="image/*"
                                className="ingredient-drawer-upload-input"
                                ref={imageRef}
                                onChange={handleImageChange}
                            />

                            {logo && (
                                <div className="ingredient-drawer-image-preview">
                                    <img src={logo} alt="Aperçu" />
                                </div>
                            )}
                        </div>

                        {/* Section: Stock & Prix */}
                        <div className="ingredient-drawer-section-header">
                            Stock & Prix
                        </div>

                        {/* Row: Prix unitaire & Quantité initiale */}
                        <div className="ingredient-drawer-row">
                            <div className="ingredient-drawer-col">
                                <label className="ingredient-drawer-label ingredient-drawer-label-required">
                                    Prix unitaire (€)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    name="prix_unitaire"
                                    value={formData.prix_unitaire}
                                    onChange={handleChange}
                                    required
                                    className="ingredient-drawer-input"
                                    placeholder="0.00"
                                />
                            </div>

                            <div className="ingredient-drawer-col">
                                <label className="ingredient-drawer-label ingredient-drawer-label-required">
                                    {editMode ? "Stock actuel" : "Quantité initiale"}
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    name="quantite_initiale"
                                    value={formData.quantite_initiale}
                                    onChange={handleChange}
                                    required={!editMode}
                                    disabled={editMode}
                                    className="ingredient-drawer-input"
                                    placeholder="0"
                                    style={editMode ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                                />
                                {editMode && (
                                    <span className="ingredient-drawer-hint">
                                        Le stock ne peut être modifié que via les mouvements
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Seuil d'alerte */}
                        <div className="ingredient-drawer-form-group">
                            <label className="ingredient-drawer-label ingredient-drawer-label-required">
                                Seuil d&apos;alerte
                            </label>
                            <input
                                type="number"
                                name="seuil_alerte"
                                value={formData.seuil_alerte}
                                onChange={handleChange}
                                required
                                className="ingredient-drawer-input"
                                placeholder="10"
                            />
                            <span className="ingredient-drawer-hint">
                                Vous serez alerté lorsque le stock descendra en dessous de ce seuil
                            </span>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="ingredient-drawer-footer">
                        <button
                            type="button"
                            className="ingredient-drawer-btn ingredient-drawer-btn-secondary"
                            onClick={onClose}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="ingredient-drawer-btn ingredient-drawer-btn-primary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="ingredient-drawer-spinner"></span>
                                    {editMode ? "Modification..." : "Création..."}
                                </>
                            ) : (
                                editMode ? "Enregistrer les modifications" : "Créer l'ingrédient"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

AddIngredient.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    editMode: PropTypes.bool,
    ingredientData: PropTypes.object,
};

export default AddIngredient;
